import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, TrendingUp, Users, Mail } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const PIE_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--secondary))",
  "hsl(var(--accent))",
  "hsl(var(--destructive))",
  "hsl(var(--muted))",
];

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalSims: 0,
    avgScore: 0,
    totalUsers: 0,
    totalLeads: 0,
  });
  const [modeData, setModeData] = useState<{ name: string; value: number }[]>([]);
  const [dailyData, setDailyData] = useState<{ date: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const [simsRes, profilesRes, leadsRes] = await Promise.all([
        supabase.from("simulacoes").select("id, created_at, score_condicao, modo"),
        supabase.from("profile").select("user_id"),
        supabase.from("lead_simulador").select("id"),
      ]);

      const sims = simsRes.data || [];
      const scored = sims.filter((s) => s.score_condicao != null);
      const avg =
        scored.reduce((a, s) => a + (s.score_condicao || 0), 0) /
        (scored.length || 1);

      setStats({
        totalSims: sims.length,
        avgScore: Math.round(avg),
        totalUsers: profilesRes.data?.length || 0,
        totalLeads: leadsRes.data?.length || 0,
      });

      // Mode distribution
      const modes: Record<string, number> = {};
      sims.forEach((s) => {
        modes[s.modo] = (modes[s.modo] || 0) + 1;
      });
      setModeData(Object.entries(modes).map(([name, value]) => ({ name, value })));

      // Daily sims (last 14 days)
      const days: Record<string, number> = {};
      sims.forEach((s) => {
        const d = new Date(s.created_at).toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
        });
        days[d] = (days[d] || 0) + 1;
      });
      const sorted = Object.entries(days)
        .map(([date, count]) => ({ date, count }))
        .slice(-14);
      setDailyData(sorted);

      setLoading(false);
    }
    load();
  }, []);

  if (loading) {
    return <p className="text-muted-foreground text-center py-12">Carregando...</p>;
  }

  const statCards = [
    { label: "Simulações", value: stats.totalSims, icon: BarChart3 },
    { label: "Score Médio", value: `${stats.avgScore}/100`, icon: TrendingUp },
    { label: "Usuários", value: stats.totalUsers, icon: Users },
    { label: "Leads", value: stats.totalLeads, icon: Mail },
  ];

  return (
    <div className="space-y-10">
      <div className="flex items-center gap-4 pb-6 border-b-4 border-foreground">
        <BarChart3 className="w-8 h-8 text-primary" />
        <h1 className="text-4xl font-heading font-black uppercase tracking-tighter">Admin::Painel</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((c) => (
          <div key={c.label} className="bg-background p-6 border-4 border-foreground shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:shadow-[6px_6px_0px_0px_rgba(255,255,255,0.15)]">
            <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.15em] text-muted-foreground mb-2">
              <c.icon className="w-4 h-4" /> {c.label}
            </div>
            <p className="text-4xl font-heading font-black tracking-tighter">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily chart */}
        <div className="bg-background p-6 border-4 border-foreground shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:shadow-[6px_6px_0px_0px_rgba(255,255,255,0.15)]">
          <h3 className="text-sm font-black uppercase tracking-[0.15em] text-muted-foreground mb-4">Simulações por Dia</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 0, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Mode pie */}
        <div className="bg-background p-6 border-4 border-foreground shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:shadow-[6px_6px_0px_0px_rgba(255,255,255,0.15)]">
          <h3 className="text-sm font-black uppercase tracking-[0.15em] text-muted-foreground mb-4">Distribuição por Modo</h3>
          <div className="h-64 flex items-center justify-center">
            {modeData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={modeData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {modeData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted-foreground font-mono uppercase text-sm">Sem dados disponíveis</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
